/**
 * API client for CleanYarn Dryclean backend.
 * All monetary values are in Kobo. Use formatNaira() for display.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.dryclean.thecleanyarn.com';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

class ApiClient {
  private accessToken: string | null = null;

  setToken(token: string | null) {
    this.accessToken = token;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: { raw?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (options.raw) {
      return { success: response.ok, data: response as unknown as T };
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  }

  // ── Auth ──
  async signup(input: { phone: string; password: string; firstName: string; lastName: string; email?: string }) {
    return this.request<{ accessToken: string; refreshToken: string; user: any }>('POST', '/v1/auth/signup', input);
  }

  async login(input: { identifier: string; password: string }) {
    return this.request<{ accessToken: string; refreshToken: string; expiresIn: number; user: any }>('POST', '/v1/auth/login', input);
  }

  async socialLogin(input: { provider: 'google' | 'apple'; idToken: string; firstName?: string; lastName?: string }) {
    return this.request<{ accessToken: string; refreshToken: string; user: any; isNewUser: boolean }>('POST', '/v1/auth/social', input);
  }

  async refreshToken(refreshToken: string) {
    return this.request<{ accessToken: string; refreshToken: string; expiresIn: number }>('POST', '/v1/auth/refresh', { refreshToken });
  }

  async logout() {
    return this.request('POST', '/v1/auth/logout');
  }

  async verifyOtp(code: string) {
    return this.request<{ verified: boolean }>('POST', '/v1/auth/verify-otp', { code });
  }

  async resendOtp() {
    return this.request<{ message: string }>('POST', '/v1/auth/resend-otp');
  }

  async resetPasswordRequest(phone: string) {
    return this.request<{ message: string }>('POST', '/v1/auth/reset-password/request', { phone });
  }

  async resetPasswordConfirm(input: { phone: string; code: string; newPassword: string }) {
    return this.request<{ message: string }>('POST', '/v1/auth/reset-password/confirm', input);
  }

  async changePassword(input: { currentPassword: string; newPassword: string }) {
    return this.request<{ message: string }>('POST', '/v1/customers/change-password', input);
  }

  // ── Profile ──
  async getProfile() {
    return this.request<any>('GET', '/v1/customers/profile');
  }

  async updateProfile(input: { firstName?: string; lastName?: string; email?: string }) {
    return this.request('PATCH', '/v1/customers/profile', input);
  }

  // ── Addresses ──
  async getAddresses() {
    return this.request<any[]>('GET', '/v1/customers/addresses');
  }

  async createAddress(input: any) {
    return this.request<{ id: string }>('POST', '/v1/customers/addresses', input);
  }

  async updateAddress(id: string, input: any) {
    return this.request('PUT', `/v1/customers/addresses/${id}`, input);
  }

  async deleteAddress(id: string) {
    return this.request('DELETE', `/v1/customers/addresses/${id}`);
  }

  // ── Orders ──
  async getOrders(params?: { page?: number; limit?: number; status?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', String(params.status));
    return this.request<{ orders: any[]; page: number; limit: number }>('GET', `/v1/orders?${query}`);
  }

  async getOrder(id: string) {
    return this.request<{ order: any; items: any[]; tracking: any[]; photos: any[]; payment: any }>('GET', `/v1/orders/${id}`);
  }

  async createOrder(input: any) {
    return this.request<{ orderId: string; orderNumber: string; pricing: any; paymentId: string }>('POST', '/v1/orders', input);
  }

  async pricingPreview(input: any) {
    return this.request<any>('POST', '/v1/orders/pricing-preview', input);
  }

  async cancelOrder(id: string) {
    return this.request('POST', `/v1/orders/${id}/cancel`);
  }

  async reorder(id: string) {
    return this.request<any>('POST', `/v1/orders/${id}/reorder`);
  }

  async reviewOrder(id: string, input: { serviceRating: number; driverRating?: number; comment?: string }) {
    return this.request('POST', `/v1/orders/${id}/review`, input);
  }

  async reportIssue(id: string, input: { type: string; description: string; photoUrls?: string[] }) {
    return this.request('POST', `/v1/orders/${id}/issue`, input);
  }

  // ── Payments ──
  async initiatePayment(input: { paymentId: string; gateway: string }) {
    return this.request<{ authorizationUrl: string; reference: string }>('POST', '/v1/payments/initiate', input);
  }

  async verifyPayment(input: { paymentId: string; gateway: string; reference: string }) {
    return this.request('POST', '/v1/payments/verify', input);
  }

  // ── Tracking ──
  async getTracking(orderId: string) {
    return this.request<any>('GET', `/v1/tracking/${orderId}`);
  }

  // ── Loyalty ──
  async getLoyalty() {
    return this.request<{ points: number; pointsValueNaira: number; history: any[] }>('GET', '/v1/customers/loyalty');
  }

  // ── Notifications ──
  async getNotifications(params?: { page?: number }) {
    const query = params?.page ? `?page=${params.page}` : '';
    return this.request<{ notifications: any[]; page: number; limit: number }>('GET', `/v1/customers/notifications${query}`);
  }

  async markNotificationRead(id: string) {
    return this.request('PATCH', `/v1/customers/notifications/${id}/read`);
  }

  async markAllNotificationsRead() {
    return this.request('POST', '/v1/customers/notifications/read-all');
  }

  // ── Subscriptions ──
  async getSubscriptions() {
    return this.request<any[]>('GET', '/v1/subscriptions');
  }

  async getSubscription(id: string) {
    return this.request<any>('GET', `/v1/subscriptions/${id}`);
  }

  async createSubscription(input: any) {
    return this.request<{ id: string }>('POST', '/v1/subscriptions', input);
  }

  async pauseSubscription(id: string) {
    return this.request('POST', `/v1/subscriptions/${id}/pause`);
  }

  async resumeSubscription(id: string) {
    return this.request('POST', `/v1/subscriptions/${id}/resume`);
  }

  async cancelSubscription(id: string) {
    return this.request('POST', `/v1/subscriptions/${id}/cancel`);
  }

  // ── Referral ──
  async getReferralInfo() {
    return this.request<any>('GET', '/v1/marketing/referral');
  }

  // ── Invoices ──
  async getInvoices(params?: { page?: number; status?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.status) query.set('status', String(params.status));
    return this.request<{ invoices: any[]; page: number; limit: number }>('GET', `/v1/customers/invoices?${query}`);
  }
}

export const api = new ApiClient();
